/**
 * Supabase auth cookie name derivation.
 *
 * @supabase/ssr derives the cookie name from the URL passed into
 * `createServerClient` / `createBrowserClient`:
 *
 *   `sb-${new URL(supabaseUrl).hostname.split('.')[0]}-auth-token`
 *
 * In Docker dev the server talks to Supabase via the internal Kong URL
 * (`http://kong:8000`) while the browser uses the host-mapped URL
 * (`http://localhost:54321`). Without intervention this produces two
 * different cookie names — the browser writes `sb-localhost-auth-token`
 * but the server looks for `sb-kong-auth-token` and never sees the
 * session, returning 401 from every API route.
 *
 * To fix this we always derive the cookie name from the public URL
 * (the one the browser uses) and pass it as `cookieOptions.name` on
 * every server-side Supabase client. In production the server and
 * browser URLs are identical, so this resolves to the same value the
 * SDK would have generated by default — no behaviour change.
 */
export function getSupabaseAuthCookieName(): string {
  const browserUrl =
    process.env.NEXT_PUBLIC_SUPABASE_URL ?? process.env.SUPABASE_URL;

  if (!browserUrl) {
    // No URL configured — return a stable fallback so the SDK does not
    // crash. Auth will not work in this state regardless, but at least
    // the cookie name is deterministic.
    return 'sb-auth-token';
  }

  try {
    const hostname = new URL(browserUrl).hostname;
    return `sb-${hostname.split('.')[0]}-auth-token`;
  } catch {
    return 'sb-auth-token';
  }
}
